// ====================================================
// src/components/block/liveStreamManagement/videoEffects/VideoEffectsPipelineManager.ts
//
// Publie les réglages vidéo (niveaux + effet actif) validés dans SourceConfigModal
// sur le VRAI flux de la source — visible sur les mini-vues, la préview du deck et
// le flux final, sans que ceux-ci en sachent quoi que ce soit : ils lisent tous le
// même MediaStream déjà emprunté au SourceRegistry (source.stream), on ne fait que
// remplacer SA PISTE vidéo en place (SourceRegistry.replaceTrack), jamais l'objet
// MediaStream lui-même. Le navigateur répercute alors le changement tout seul sur
// chaque <video> déjà branché sur ce stream — aucun autre fichier à toucher.
//
// PIÈGE ÉVITÉ (cf. recherche préalable) : SourceRegistry.injectStream() sur le MÊME
// id que la caméra brute ferait stopper les pistes brutes par l'acquirer AVANT de les
// remplacer — tuant le flux qui nourrit justement notre propre canvas. On emprunte
// donc notre propre piste caméra INDÉPENDANTE (MediaStreamTrack.clone(), qui survit
// à l'arrêt de son original) avant de publier le résultat via replaceTrack (qui,
// contrairement à injectStream, ne passe jamais par l'acquirer).
//
// Portée : uniquement les sources qui possèdent un vrai MediaStream dans le registry
// (device/screen/remote). Les sources HLS ('url' — Playlist, OBS/RTMP...) sont
// décodées en direct par hls.js sur une <video>, il n'existe aucun MediaStream à
// modifier pour elles côté registry — la préview locale de la modale peut les
// composer (cf. SourceConfigModal), mais rien ne peut se publier en aval pour elles.
// ====================================================

import { SourceRegistry } from '../../../../dataTypes/liveStreamManagment/sourceRegistry/SourceRegistry';
import { VIDEO_EFFECTS } from './registry';
import { segmentPersonInVideo, paintPersonMask } from './PersonSegmenter';
import { detectFaceInVideo, FaceTrackingResult } from './FaceTracker';
import { isFaceDetectionDue, isSegmentationCappable } from './faceTrackingBudget';
import { detectHandsInVideo, HandTrackingResult } from './HandTracker';
import { disposeAvatarRenderer } from './AvatarRenderer';
import { paintProcessedFrame, levelsAreDefault, VideoLevels } from './compositeFrame';

export interface VideoEffectsSettings {
  videoLevels: VideoLevels;
  /** Au plus un id par groupe (cf. VideoEffect.group) — l'exclusivité mutuelle au sein
   * d'un groupe est garantie côté SourceConfigModal, pas revérifiée ici. */
  activeEffectIds: string[];
  effectConfigs: Record<string, unknown>;
}

function settingsAreDefault(settings: VideoEffectsSettings): boolean {
  return levelsAreDefault(settings.videoLevels) && settings.activeEffectIds.length === 0;
}

interface PipelineEntry {
  release: () => void;
  /** Clone indépendant de la piste caméra brute — jamais stoppé tant que ce pipeline
   * vit, même après que la piste d'origine a été remplacée dans le stream public. */
  rawTrack: MediaStreamTrack;
  /** MediaStream d'édition, créé UNE fois — cf. getRawStreamForEditing. */
  editStream: MediaStream;
  hiddenVideo: HTMLVideoElement;
  structureCanvas: HTMLCanvasElement;
  outputCanvas: HTMLCanvasElement;
  scratch: HTMLCanvasElement;
  maskCanvas: HTMLCanvasElement;
  settings: VideoEffectsSettings;
  personAlpha: HTMLCanvasElement | null;
  segmenting: boolean;
  /** Instant du dernier départ de segmentation — pour plafonner la cadence quand seul l'Avatar la demande. */
  lastSegAt?: number;
  faceLandmarks: FaceTrackingResult[];
  tracking: boolean;
  handLandmarks: HandTrackingResult[];
  handTracking: boolean;
  rafId: number;
  cancelled: boolean;
}

const pipelines = new Map<string, PipelineEntry>();

async function createPipeline(registry: SourceRegistry, sourceId: string, settings: VideoEffectsSettings): Promise<PipelineEntry | null> {
  const handle = await registry.borrow(sourceId);
  const rawVideoTrack = handle.stream.getVideoTracks()[0];
  if (!rawVideoTrack) { handle.release(); return null; }
  const rawTrack = rawVideoTrack.clone();

  const hiddenVideo = document.createElement('video');
  hiddenVideo.muted = true;
  hiddenVideo.playsInline = true;
  hiddenVideo.srcObject = new MediaStream([rawTrack]);
  await hiddenVideo.play().catch(() => {});

  const structureCanvas = document.createElement('canvas');
  const outputCanvas    = document.createElement('canvas');
  const scratch         = document.createElement('canvas');
  const maskCanvas      = document.createElement('canvas');
  const outputCtx = outputCanvas.getContext('2d');
  if (!outputCtx) { hiddenVideo.srcObject = null; handle.release(); return null; }

  const entry: PipelineEntry = {
    release: handle.release, rawTrack, editStream: new MediaStream([rawTrack]), hiddenVideo, structureCanvas, outputCanvas, scratch, maskCanvas,
    settings, personAlpha: null, segmenting: false, faceLandmarks: [], tracking: false,
    handLandmarks: [], handTracking: false, rafId: 0, cancelled: false,
  };

  const tick = () => {
    if (entry.cancelled) return;
    const v = hiddenVideo;
    if (v.videoWidth > 0 && v.readyState >= 2) {
      const w = v.videoWidth, h = v.videoHeight;
      if (outputCanvas.width !== w || outputCanvas.height !== h) { outputCanvas.width = w; outputCanvas.height = h; }

      const activeEffects = entry.settings.activeEffectIds
        .map(id => VIDEO_EFFECTS.find(e => e.id === id))
        .filter((e): e is typeof VIDEO_EFFECTS[number] => !!e);
      const segEffects   = activeEffects.filter(e => e.needsSegmentationFor(entry.settings.effectConfigs[e.id] ?? e.defaultConfig));
      const needsSeg     = segEffects.length > 0;
      // Cadence plafonnée quand seuls l'Avatar et l'Arrière-plan demandent le détourage
      // (isSegmentationCappable) : Maquillage, s'il est actif, garde son rythme d'origine.
      const segAvatarOnly = isSegmentationCappable(segEffects.map(e => e.id));
      const needsTracking = activeEffects.some(e => e.needsFaceTracking);
      const needsHandTracking = activeEffects.some(e => e.needsHandTracking);

      if (needsSeg) {
        const segNow = performance.now();
        if (!entry.segmenting && (!segAvatarOnly || isFaceDetectionDue(segNow, entry.lastSegAt ?? null))) {
          entry.segmenting = true;
          entry.lastSegAt = segNow;
          segmentPersonInVideo(v, segNow)
            .then(mask => { if (!entry.cancelled && mask) { paintPersonMask(mask, maskCanvas); entry.personAlpha = maskCanvas; } })
            .catch(() => {})
            .finally(() => { entry.segmenting = false; });
        }
      } else {
        entry.personAlpha = null;
      }

      if (needsTracking) {
        if (!entry.tracking) {
          entry.tracking = true;
          detectFaceInVideo(v, performance.now())
            .then(result => { if (!entry.cancelled) entry.faceLandmarks = result; })
            .catch(() => {})
            .finally(() => { entry.tracking = false; });
        }
      } else {
        entry.faceLandmarks = [];
      }

      if (needsHandTracking) {
        if (!entry.handTracking) {
          entry.handTracking = true;
          detectHandsInVideo(v, performance.now())
            .then(result => { if (!entry.cancelled) entry.handLandmarks = result; })
            .catch(() => {})
            .finally(() => { entry.handTracking = false; });
        }
      } else {
        entry.handLandmarks = [];
      }

      paintProcessedFrame({
        outputCtx, structureCanvas, width: w, height: h, frame: v,
        activeEffects: activeEffects.map(effect => ({
          effect, config: entry.settings.effectConfigs[effect.id] ?? effect.defaultConfig,
        })),
        levels: entry.settings.videoLevels, personAlpha: entry.personAlpha,
        faceLandmarks: entry.faceLandmarks, handLandmarks: entry.handLandmarks, scratch,
      });
    }
    entry.rafId = requestAnimationFrame(tick);
  };
  entry.rafId = requestAnimationFrame(tick);

  const processedTrack = outputCanvas.captureStream(25).getVideoTracks()[0];
  registry.replaceTrack(sourceId, processedTrack);

  return entry;
}

function destroyPipeline(registry: SourceRegistry, sourceId: string, entry: PipelineEntry): void {
  entry.cancelled = true;
  cancelAnimationFrame(entry.rafId);
  // Rend la caméra telle quelle — le clone est resté vivant tout du long, pas besoin de
  // rouvrir getUserMedia pour « débraucher » le canvas.
  registry.replaceTrack(sourceId, entry.rawTrack);
  // Libère le contexte WebGL de l'Avatar tout de suite plutôt que d'attendre que
  // hiddenVideo (jeté juste après) soit ramassé par le GC — cf. disposeAvatarRenderer.
  disposeAvatarRenderer(entry.hiddenVideo);
  entry.hiddenVideo.pause();
  entry.hiddenVideo.srcObject = null;
  entry.release();
  pipelines.delete(sourceId);
}

/**
 * Applique (ou retire) les réglages vidéo d'une source sur son VRAI flux. Idempotent :
 * un appel avec des réglages par défaut sur une source sans pipeline actif ne fait rien.
 */
export async function applyVideoEffectsSettings(registry: SourceRegistry, sourceId: string, settings: VideoEffectsSettings): Promise<void> {
  const existing = pipelines.get(sourceId);
  const isDefault = settingsAreDefault(settings);

  if (existing) {
    if (isDefault) { destroyPipeline(registry, sourceId, existing); return; }
    existing.settings = settings;
    return;
  }

  if (isDefault) return;

  try {
    const entry = await createPipeline(registry, sourceId, settings);
    if (entry) pipelines.set(sourceId, entry);
  } catch (err) {
    console.warn(`[VideoEffectsPipelineManager] échec de création du pipeline pour ${sourceId} :`, err);
  }
}

/**
 * Flux caméra brut (non traité), pour que SourceConfigModal continue de prévisualiser
 * et de segmenter la source réelle — pas la version déjà traitée que publie le pipeline
 * sous le même id, qu'on ré-éditerait sinon en aveugle par-dessus elle-même.
 */
export function getRawStreamForEditing(sourceId: string): MediaStream | null {
  const entry = pipelines.get(sourceId);
  // Identité STABLE : la modale l'appelle à chaque rendu et s'en sert comme dépendance
  // d'effet / pour srcObject — un nouvel objet à chaque appel relançait le compositeur
  // et rechargeait la <video> en boucle, le détourage ne convergeait jamais.
  return entry ? entry.editStream : null;
}

export function hasActiveVideoEffectsPipeline(sourceId: string): boolean {
  return pipelines.has(sourceId);
}

/**
 * Détruit TOUS les pipelines actifs — à appeler au changement de chaîne (cf.
 * LiveStreamManager, sur changement de channelUuid). `pipelines` est un singleton
 * MODULE-LEVEL, jamais recréé au remontage de LiveStreamManager (qui, lui, remonte
 * bien par chaîne) : sans cet appel explicite, un effet vidéo activé sur une source
 * dans une chaîne continuait de tourner — et de remplacer la piste de cette même
 * source physique (webcam, slot d'écran) — après bascule vers une AUTRE chaîne,
 * sans aucune action de l'opérateur dans la nouvelle chaîne (constaté explicitement,
 * même symptôme que le cloisonnement des effets audio par chaîne).
 */
export function destroyAllVideoEffectsPipelines(registry: SourceRegistry): void {
  pipelines.forEach((entry, sourceId) => destroyPipeline(registry, sourceId, entry));
}
